/**
 * Görüşme Notları Store Slice
 * 
 * Görüşme notlarının yönetilmesi için state ve fonksiyonlar içerir.
 * NOT: Bu versiyonda loadNotes ve loadNotesViaSocket işlevleri devre dışı bırakılmıştır.
 * Sadece not kaydetme işlevleri aktiftir.
 */

import { toast } from 'react-hot-toast';
import { api } from '../../services/authService';
import logger from '../../utils/logger';

const createNotesSlice = (set, get) => ({
    // State
    notes: [],               // Veritabanından yüklenen notlar (artık kullanılmıyor)
    savedNotes: [],          // Yerel olarak kaydedilen notlar
    currentNote: '',         // Şu anda düzenlenen not
    isLoading: false,        // Not yükleme durumu
    noteError: null,         // Not işlemlerinde hata

    // Not metnini ayarla
    setNotes: (text) => set({ currentNote: text }),

    // Notları yükle (API) - Artık kullanılmıyor, boş işlev
    loadNotes: async (sessionId) => {
        // Herhangi bir işlem yapılmıyor
        return;
    },

    // Socket ile notları yükleme - Artık kullanılmıyor, boş işlev
    loadNotesViaSocket: (sessionId) => {
        // Herhangi bir işlem yapılmıyor
        return;
    },

    // Not kaydet (API)
    saveNote: async () => {
        const { currentNote, sessionId } = get();
        const { callDuration } = get();

        if (!currentNote.trim() || !sessionId) {
            return;
        }

        try {
            set({ isLoading: true, noteError: null });

            // API çağrısı
            const response = await api.post('notes', {
                session_id: sessionId,
                text: currentNote,
                duration: callDuration || 0
            });

            if (response.data && response.data.status === 'success') {
                // Not başarıyla kaydedildi, state'i temizle
                set({
                    currentNote: '',
                    isLoading: false
                });

            }
        } catch (error) {
            logger.error('Not kaydedilirken hata:', error);

            // API başarısız olursa, notu yerel olarak kaydet
            const newNote = {
                id: `local-${Date.now()}`,
                text: currentNote,
                duration: callDuration || 0,
                timestamp: new Date(),
                isLocal: true
            };

            set(state => ({
                savedNotes: [...state.savedNotes, newNote],
                currentNote: '',
                noteError: error.response?.data?.message || 'Not kaydedilemedi',
                isLoading: false
            }));

        }
    },

    // Socket ile not kaydet
    saveNoteViaSocket: () => {
        const { socket, currentNote, sessionId } = get();
        const { callDuration } = get();

        if (!socket || !currentNote.trim() || !sessionId) {
            return;
        }

        // Kimlik doğrulama durumunu kontrol et
        if (!socket.connected) {

            // Not başarısız olursa, notu yerel olarak kaydet
            const newNote = {
                id: `local-${Date.now()}`,
                text: currentNote,
                duration: callDuration || 0,
                timestamp: new Date(),
                isLocal: true
            };

            set(state => ({
                savedNotes: [...state.savedNotes, newNote],
                currentNote: '',
                noteError: 'Sunucu bağlantısı yok',
                isLoading: false
            }));

            return;
        }

        set({ isLoading: true });

        // Socket'in kullanıcı kimliği var mı kontrol et
        // Kimlik bilgileri yoksa önce authenticate ol
        const { user = {}, token } = get();

        if (token && user.id) {
            // Kimlik doğrulama işlemini gerçekleştir
            socket.emit('authenticate', {
                token,
                userId: user.id
            });
        }

        // Socket'e kaydetme isteği gönder
        socket.emit('add-note', {
            sessionId,
            text: currentNote,
            duration: callDuration || 0,
            isImportant: false
        });

        // Yerel state'i güncelle
        set({ currentNote: '', isLoading: false });

        // Başarılı bir şekilde kaydedildi mesajı göster
    },

    // Socket.io not event'lerini dinle - Artık kullanılmıyor, boş işlev
    setupNoteListeners: () => {
        // Herhangi bir dinleyici kurulmuyor
        return;
    },

    // Oturum odasına katıl - Not ekleme için gerekli olduğundan korunuyor
    joinNoteRoom: (sessionId) => {
        const { socket } = get();

        if (!socket || !sessionId) {
            return;
        }

        socket.emit('join-session-room', { sessionId });
    },

    // Oturum odasından ayrıl - Not ekleme için gerekli olduğundan korunuyor
    leaveNoteRoom: (sessionId) => {
        const { socket } = get();

        if (!socket || !sessionId) {
            return;
        }

        socket.emit('leave-session-room', { sessionId });
    },

    // Not slice'ını temizle
    clearNotes: () => {
        set({
            notes: [],
            savedNotes: [],
            currentNote: '',
            isLoading: false,
            noteError: null
        });
    }
});

export default createNotesSlice; 
